/**
 * Wave 29 Chunk 8e — Sales-doc editor (full page, replaces modal).
 *
 * Routes:
 *   /customers/:cid/sales-docs/new?category=quote|sales_order|invoice|...   — create
 *   /sales-documents/:did/edit                                              — edit
 *
 * Implements DESIGN_CONTRACT.md Rule 0 (no small modals) and Rule 1
 * (Internal/DNU vs Customer-facing/CF audience choice is unmissable).
 *
 * Layout:
 *   Header (back link, title, save button)
 *   Audience block (forced for quote/sales_order; locked Customer for invoices)
 *   Document fields (category, title, number, date, amount, status, notes)
 *   Payment block (invoices only, when canMarkPaid)
 *   Upload picker (NEW only — switches between r2 upload + plain manual entry)
 */
import { useEffect, useMemo, useState } from "react";
import VoiceTextarea from "../components/VoiceTextarea";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Save, Loader2, AlertTriangle, FileText, ShieldAlert,
  Upload as UploadIcon, CheckCircle2, Circle, XCircle,
  ClipboardList, Sparkles, ExternalLink, Package, User as UserIcon,
  FileWarning,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import DatePickerInput from "../components/DatePickerInput";
import SalesDocScanner from "../components/SalesDocScanner";
import SearchableSelect from "../components/SearchableSelect";
import { EntityShell } from "../components/shell";
import EntityLinksPanel from "../components/EntityLinksPanel";

const CATEGORIES = [
  { key: "quote",         label: "Quote" },
  { key: "sales_order",   label: "Sales order" },
  { key: "purchase_order",label: "Purchase order" },
  { key: "invoice",       label: "Invoice" },
  { key: "credit_note",   label: "Credit note" },
  { key: "statement",     label: "Statement" },
];

const STATUS_OPTIONS = {
  quote: ["draft", "sent", "accepted", "rejected", "expired"],
  sales_order: ["draft", "confirmed", "in_progress", "delivered", "closed", "cancelled"],
  purchase_order: ["draft", "issued", "received", "closed", "cancelled"],
  invoice: ["draft", "issued", "paid", "void"],
  credit_note: ["draft", "issued"],
  statement: ["draft", "issued"],
};

const FORCED_CUSTOMER_CATS = new Set(["invoice", "credit_note", "statement"]);
const ALLOW_AUDIENCE_PICK = new Set(["quote", "sales_order"]);

export default function SalesDocEditor({ mode }) {
  // mode: "new" or "edit". Derived from the route in App.js.
  const nav = useNavigate();
  const { cid, did } = useParams();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const canMarkPaid = user && ["owner", "admin", "manager"].includes(user.role);

  const [loading, setLoading] = useState(mode === "edit");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [customerId, setCustomerId] = useState(cid || "");
  const [customer, setCustomer] = useState(null);
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    title: "",
    document_number: "",
    document_date: new Date().toISOString().slice(0, 10),
    amount: "",
    notes: "",
    category: sp.get("category") || "quote",
    status: "",
    payment_status: "",
    payment_link: "",
    audience: ALLOW_AUDIENCE_PICK.has(sp.get("category") || "quote") ? "" : "customer",
  });
  // NEW mode: optional file upload. There is no "single vs bulk" mode —
  // the operator drops 1..N files and we always go through the multi-file
  // endpoint, with per-file progress + OCR status shown inline (Iter 234).
  const [files, setFiles] = useState([]);
  // Per-file status row: { name, size, status: 'pending'|'uploading'|'uploaded'|'failed', error? }
  const [fileStatus, setFileStatus] = useState([]);
  const [uploadPct, setUploadPct] = useState(0);
  // OCR'd line items (parsed from the scan) — persisted with the doc.
  const [lineItems, setLineItems] = useState(null);

  // Apply a Hidro scan suggestion to the form state. Only fills empty
  // fields by default so we don't clobber whatever the owner manually typed.
  const applyScan = (s) => {
    setForm((prev) => ({
      ...prev,
      category: s.category || prev.category,
      document_number: s.document_number || prev.document_number,
      document_date: s.document_date || prev.document_date,
      amount: prev.amount || (s.amount != null ? String(s.amount) : prev.amount),
      title: prev.title || s.title || "",
    }));
    setLineItems(s.line_items || []);
    // If the OCR picked up the bill-to confidently AND we're on a blank
    // customer route, hop us over. Don't auto-switch when we're already
    // pinned to a customer — owner sees the mismatch warning instead.
    if (!customerId && s.bill_to_match?.matched_customer_id) {
      setCustomerId(s.bill_to_match.matched_customer_id);
    }
  };

  // Load existing doc + customer.
  useEffect(() => {
    if (mode !== "edit" || !did) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/sales-documents/${did}`);
        setForm({
          title: data.title || "",
          document_number: data.document_number || "",
          document_date: data.document_date || "",
          amount: data.amount ?? "",
          notes: data.notes || "",
          category: data.category,
          status: data.status || "",
          payment_status: data.payment_status || "",
          payment_link: data.payment_link || "",
          audience: data.audience || "customer",
        });
        setCustomerId(data.customer_id);
        setProjectId(data.project_id || "");
        setLineItems(Array.isArray(data.line_items) ? data.line_items : null);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not load document.");
      } finally { setLoading(false); }
    })();
  }, [mode, did]);

  // Resolve customer name for context.
  useEffect(() => {
    if (!customerId) return;
    api.get(`/customers/${customerId}`)
      .then(({ data }) => setCustomer(data))
      .catch(() => {});
  }, [customerId]);

  // Wave 29 Chunk 10n — load this customer's projects so the operator can
  // link the sales doc to a project (optional). Open / on_hold projects
  // come first so the most likely candidates are at the top.
  useEffect(() => {
    if (!customerId) { setProjects([]); return; }
    api.get(`/projects?customer_id=${customerId}&limit=100`)
      .then(({ data }) => {
        const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
        const rank = (p) => {
          const s = (p.status || "").toLowerCase();
          if (s === "open" || s === "active" || s === "in_progress") return 0;
          if (s === "on_hold") return 1;
          if (s === "closed" || s === "completed") return 2;
          return 3;
        };
        items.sort((a, b) => rank(a) - rank(b));
        setProjects(items);
      })
      .catch(() => setProjects([]));
  }, [customerId]);

  // Coerce audience when category changes (e.g. → invoice forces customer).
  useEffect(() => {
    if (FORCED_CUSTOMER_CATS.has(form.category) && form.audience !== "customer") {
      setForm((s) => ({ ...s, audience: "customer" }));
    }
  }, [form.category, form.audience]);

  const setF = (patch) => setForm((s) => ({ ...s, ...patch }));

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    // 2026-02 — Dirk: drop the compulsory-title gate. Title now defaults
    // server-side to filename stem and is auto-filled by background OCR.
    if (ALLOW_AUDIENCE_PICK.has(form.category) && !form.audience) {
      return setErr("Pick an audience: Internal (DNU) or Customer-facing (CF).");
    }
    setBusy(true);
    try {
      const base = {
        title: (form.title || "").trim() || (files[0]?.name?.replace(/\.[^.]+$/, "") || ""),
        document_number: form.document_number || null,
        document_date: form.document_date || null,
        amount: form.amount ? Number(form.amount) : null,
        notes: form.notes || "",
        category: form.category,
        status: form.status || null,
        audience: form.audience || "customer",
      };
      if (form.category === "invoice" && canMarkPaid) {
        base.payment_status = form.payment_status || null;
        base.payment_link = form.payment_link || null;
      } else {
        base.payment_status = null;
        base.payment_link = null;
      }

      if (mode === "edit") {
        await api.put(`/sales-documents/${did}`, { ...base, customer_id: customerId, project_id: projectId || null, line_items: lineItems || undefined });
        toast.success("Document saved.");
      } else if (files.length > 0) {
        // Iter 234 — unified file upload. Always uses the multi-file
        // endpoint regardless of count (1 or 50). Real upload progress
        // via axios `onUploadProgress`; per-file rows update afterwards
        // from the response payload.
        setFileStatus(files.map((f) => ({
          name: f.name, size: f.size, status: "uploading",
        })));
        setUploadPct(0);
        const fd = new FormData();
        fd.append("customer_id", customerId);
        fd.append("category", form.category);
        fd.append("audience", form.audience || "customer");
        if (projectId) fd.append("project_id", projectId);
        files.forEach((f) => fd.append("files", f, f.name));
        const { data } = await api.post("/sales-documents/bulk-upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 600_000,
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            setUploadPct(Math.min(99, Math.round((evt.loaded / evt.total) * 100)));
          },
        });
        setUploadPct(100);
        // Mark every file that came back as "uploaded"; anything missing
        // (count < files.length) is marked failed so the operator can retry.
        const createdNames = new Set((data?.created || []).map((d) => d.file_name));
        setFileStatus((prev) => prev.map((row) => ({
          ...row,
          status: createdNames.has(row.name) ? "uploaded" : "failed",
          error: createdNames.has(row.name) ? null : "Upload skipped (size or storage error).",
        })));
        const n = data?.count || 0;
        toast.success(
          n === 1
            ? "1 document uploaded — OCR running in background."
            : `${n} documents uploaded — OCR running in background.`,
        );
      } else {
        // Metadata-only create (no file attached) — quote / SO / etc.
        await api.post("/sales-documents", { ...base, customer_id: customerId, project_id: projectId || null, line_items: lineItems || undefined });
        toast.success("Document created.");
      }
      nav(`/customers/${customerId}`);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Save failed.");
      setFileStatus((prev) => prev.map((row) => (
        row.status === "uploading" ? { ...row, status: "failed", error: "Upload failed." } : row
      )));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="h-32 hv-card animate-pulse" data-testid="sales-doc-editor-loading" />;

  const audienceLocked = FORCED_CUSTOMER_CATS.has(form.category);

  return (
    <EntityShell
      kindLabel="Sales document"
      title={mode === "edit" ? `${(form.category || "sales_document").replaceAll("_", " ")} · ${form.title || form.document_number || "Sales document"}` : "Upload sales document"}
      status={form.category ? { label: form.category.replaceAll("_", " "), tone: "slate" } : undefined}
      customerLink={customer && customerId ? {
        label: `View customer · ${customer.name || customer.company_name || customerId.slice(0, 8)}`,
        to: `/customers/${customerId}`,
      } : undefined}
      backTo={customerId ? `/customers/${customerId}` : "/customers"}
    >
    <form onSubmit={submit} className={`space-y-4 ${mode === "edit" ? "max-w-5xl" : "max-w-3xl"}`} data-testid="sales-doc-editor-page">

      {err && (
        <div className="hv-card !p-3 text-xs text-rose-300 flex items-center gap-2" data-testid="sales-doc-editor-error">
          <AlertTriangle className="w-4 h-4" /> {err}
        </div>
      )}

      {/* STEP 1 — Audience picker. Iter 290r: this MUST come before any
          file action. For quote / sales_order it is hard-required; for
          invoice / credit_note / statement it is auto-locked to CF. */}
      <section className="hv-card !p-4 space-y-3" data-testid="sales-doc-editor-audience">
        <header className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-300" />
            Step 1 · Audience
          </h2>
          {audienceLocked && (
            <span className="text-[10px] text-[#647087]">Money docs are always customer-facing.</span>
          )}
        </header>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <AudienceTile
            label="🔴 Internal (DNU)"
            description="Detailed engineering doc with line items, picking, pricing. Used for project budget + procurement + OCR. NEVER shared with customer."
            selected={form.audience === "internal"}
            disabled={audienceLocked}
            onSelect={() => setF({ audience: "internal" })}
            testId="sales-doc-audience-internal"
            tone="rose"
          />
          <AudienceTile
            label="🟢 Customer-facing (CF)"
            description="Summary doc safe to share via portal. Invoices are issued against these."
            selected={form.audience === "customer"}
            disabled={false}
            onSelect={() => setF({ audience: "customer" })}
            testId="sales-doc-audience-customer"
            tone="emerald"
          />
        </div>
        {ALLOW_AUDIENCE_PICK.has(form.category) && !form.audience && (
          <div
            className="text-[10px] text-amber-300/90 bg-amber-400/10 border border-amber-400/30 rounded px-2 py-1"
            data-testid="sales-doc-editor-audience-required-hint"
          >
            Pick DNU or CF first — file upload + OCR is disabled until you do.
          </div>
        )}
      </section>

      {/* STEP 2 — OCR scan & auto-fill (NEW only). Iter 290r: gated on
          audience picked so the user can't dump a file and "forget" to
          tag it. The scanner now ALSO attaches the file to the parent's
          files[] state so submitting actually uploads it (previously it
          only ran OCR-preview and the file was lost on submit). */}
      {mode !== "edit" && (
        <div
          className={ALLOW_AUDIENCE_PICK.has(form.category) && !form.audience ? "opacity-50 pointer-events-none select-none" : ""}
          aria-disabled={ALLOW_AUDIENCE_PICK.has(form.category) && !form.audience}
          data-testid="sales-doc-editor-scanner-wrap"
        >
          <SalesDocScanner
            customerId={customerId}
            initialCategory={form.category}
            onApply={applyScan}
            onFilePicked={(f) => {
              // Iter 290r — make the scanner's picked file the actual
              // upload. Replaces empty `files[]` so submit() runs the
              // bulk-upload branch (with audience baked in).
              if (f) {
                setFiles([f]);
                setFileStatus([{ name: f.name, size: f.size, status: "pending" }]);
              }
            }}
          />
        </div>
      )}

      {/* Wave 29 Chunk 10n — Project link + profitability */}
      <ProjectLinkCard
        projects={projects}
        projectId={projectId}
        onChange={setProjectId}
        category={form.category}
      />
      <ProfitabilityCard
        lineItems={lineItems}
        category={form.category}
        amount={form.amount}
      />

      {/* Iter 411 (Option A) — Unified sales-doc detail view. When editing
          an existing doc, surface every field the OCR pipeline extracted
          (line items, bill-to, totals, statement snapshot) right on this
          page, so the JobCard → Sales Order link no longer opens an empty
          form and users don't need to bounce back to Customer Accounts to
          see what was scanned. */}
      {mode === "edit" && did && (
        <ExtractedContentPanel
          did={did}
          customerId={customerId}
          category={form.category}
          lineItems={lineItems}
        />
      )}

      {/* Document fields */}
      <section className="hv-card !p-4 space-y-3" data-testid="sales-doc-editor-fields">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Category *">
            <select
              value={form.category}
              onChange={(e) => setF({ category: e.target.value, status: "" })}
              className="hv-input text-sm w-full"
              disabled={mode === "edit"}
              data-testid="sales-doc-editor-category"
            >
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select
              value={form.status}
              onChange={(e) => setF({ status: e.target.value })}
              className="hv-input text-sm w-full"
              data-testid="sales-doc-editor-status"
            >
              <option value="">— Auto —</option>
              {(STATUS_OPTIONS[form.category] || []).map((s) => (
                <option key={s} value={s}>{s.replace("_", " ")}</option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Title (optional — auto-filled from OCR)">
          <input
            type="text"
            value={form.title}
            onChange={(e) => setF({ title: e.target.value })}
            className="hv-input text-sm w-full"
            placeholder="Leave blank to auto-fill from the uploaded file"
            data-testid="sales-doc-editor-title"
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Number / Ref">
            <input
              type="text"
              value={form.document_number}
              onChange={(e) => setF({ document_number: e.target.value })}
              className="hv-input text-sm w-full font-mono"
              data-testid="sales-doc-editor-number"
            />
          </Field>
          <Field label="Date">
            <DatePickerInput
              value={form.document_date}
              onChange={(v) => setF({ document_date: v })}
              testId="sales-doc-editor-date"
            />
          </Field>
          <Field label="Amount">
            <input
              type="number"
              step="0.01"
              value={form.amount}
              onChange={(e) => setF({ amount: e.target.value })}
              className="hv-input text-sm w-full font-mono"
              data-testid="sales-doc-editor-amount"
            />
          </Field>
        </div>
        <Field label="Notes">
          <VoiceTextarea
            value={form.notes}
            onChange={(v) => setF({ notes: v })}
            rows={3}
            context="sales_doc_notes"
            testIdPrefix="sales-doc-editor-notes-voice"
            data-testid="sales-doc-editor-notes"
          />
        </Field>
      </section>

      {/* Payment fields (invoices only) */}
      {form.category === "invoice" && canMarkPaid && (
        <section className="hv-card !p-4 space-y-3 border border-amber-500/20 bg-amber-500/5" data-testid="sales-doc-editor-payment">
          <h2 className="text-xs uppercase tracking-[0.2em] text-amber-300 font-bold">Payment tracking</h2>
          <Field label="Status">
            <select
              value={form.payment_status}
              onChange={(e) => setF({ payment_status: e.target.value })}
              className="hv-input text-sm w-full"
              data-testid="sales-doc-editor-payment-status"
            >
              <option value="">— No status —</option>
              <option value="outstanding">Outstanding (show Pay-now link)</option>
              <option value="paid">Paid</option>
            </select>
          </Field>
          {form.payment_status === "outstanding" && (
            <Field label="Pay-now link (optional)">
              <input
                type="url"
                value={form.payment_link}
                onChange={(e) => setF({ payment_link: e.target.value })}
                className="hv-input text-sm w-full"
                placeholder="https://pay.example.com/..."
                data-testid="sales-doc-editor-payment-link"
              />
            </Field>
          )}
        </section>
      )}

      {/* File attachment (NEW only). Drop 1..N files — the upload always
          uses the multi-file endpoint and shows live per-file progress
          (Iter 234 — no more "single vs bulk" distinction). */}
      {mode !== "edit" && (
        <section className="hv-card !p-4 space-y-3" data-testid="sales-doc-editor-file">
          <h2 className="text-xs uppercase tracking-[0.2em] text-[#647087] font-bold flex items-center gap-2">
            <UploadIcon className="w-3.5 h-3.5" /> Attach files (optional · drop one or many)
          </h2>
          <input
            type="file"
            multiple
            onChange={(e) => {
              const arr = Array.from(e.target.files || []);
              setFiles(arr);
              setFileStatus(arr.map((f) => ({
                name: f.name, size: f.size, status: "pending",
              })));
              setUploadPct(0);
            }}
            className="text-xs text-[#a0aabf]"
            data-testid="sales-doc-editor-file-input"
          />
          {files.length > 0 && (
            <div className="text-[11px] text-[#a0aabf] space-y-2" data-testid="sales-doc-editor-files-list">
              <p className="text-[#1FB6C1]">
                {files.length === 1 ? "1 file" : `${files.length} files`} ready — all save as
                {" "}<span className="font-mono">{form.category}</span>
                {ALLOW_AUDIENCE_PICK.has(form.category) && form.audience && (
                  <> · <span className="font-mono uppercase">{form.audience === "internal" ? "DNU" : "CF"}</span></>
                )}
                . OCR fills the fields in the background.
              </p>
              {/* Overall upload progress bar */}
              {(busy || uploadPct > 0) && (
                <div className="space-y-1" data-testid="sales-doc-editor-progress">
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div
                      className="h-full bg-[#1FB6C1] transition-all duration-150"
                      style={{ width: `${uploadPct}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-[#647087]">
                    {uploadPct < 100 ? `Uploading… ${uploadPct}%` : "Upload complete — OCR queued."}
                  </p>
                </div>
              )}
              {/* Per-file rows */}
              <ul className="mt-1 max-h-48 overflow-y-auto space-y-1">
                {(fileStatus.length ? fileStatus : files.map((f) => ({
                  name: f.name, size: f.size, status: "pending",
                }))).map((row, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-2 text-[10px]"
                    data-testid={`sales-doc-editor-file-row-${i}`}
                  >
                    <FileStatusIcon status={row.status} />
                    <span className="truncate flex-1 text-[#a0aabf]">{row.name}</span>
                    <span className="text-[#647087] tabular-nums">
                      {Math.round(row.size / 1024)} KB
                    </span>
                    <span
                      className={
                        row.status === "uploaded" ? "text-emerald-300"
                          : row.status === "uploading" ? "text-cyan-300"
                          : row.status === "failed" ? "text-rose-300"
                          : "text-[#647087]"
                      }
                      data-testid={`sales-doc-editor-file-status-${i}`}
                    >
                      {row.status === "uploaded" ? "Uploaded · OCR queued"
                        : row.status === "uploading" ? "Uploading"
                        : row.status === "failed" ? (row.error || "Failed")
                        : "Pending"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Cross-entity links — Iter 290zb. Only on existing docs of types
          that actually trigger picking / job-card workflows (sales orders,
          quotes, and invoices). Hidden on create / DNU drafts where the
          ids don't exist yet. */}
      {mode === "edit" && did && ["sales_order", "quote", "invoice"].includes(form.category) && (
        <EntityLinksPanel
          entityType="sales_doc"
          entityId={did}
          linkTo={["jobcard", "picking"]}
        />
      )}

      {/* Iter 395 — Push-to-Picking-Slip action. Sales orders drive
          picking, so a direct button here is the shortest path when a
          rep has just captured/edited the SO and wants to hand it to
          the warehouse. Behaviour:
            • 0 linked picking slips → POST /picking/from-sales-order
              (creates a fresh slip, auto-cross-links to any JC already
              tied to this SO).
            • 1+ linked → dropdown lets the user pick "Add to <existing>"
              (idempotent qty accumulation) OR "Create new picking slip".
          Only visible for sales_order category in edit mode. */}
      {mode === "edit" && did && form.category === "sales_order" && (
        <PushToPickingSlipCard salesDocId={did} customerId={cid} />
      )}

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
        <Link
          to={customerId ? `/customers/${customerId}` : "/customers"}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="sales-doc-editor-cancel"
        >
          Cancel
        </Link>
        <button
          type="submit"
          disabled={busy}
          className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
          data-testid="sales-doc-editor-save"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {mode === "edit"
            ? "Save changes"
            : files.length > 1
            ? `Upload ${files.length} documents`
            : files.length === 1
            ? "Upload document"
            : "Create document"}
        </button>
      </div>
    </form>
    </EntityShell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">{label}</div>
      {children}
    </label>
  );
}

function FileStatusIcon({ status }) {
  if (status === "uploaded") {
    return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />;
  }
  if (status === "uploading") {
    return <Loader2 className="w-3.5 h-3.5 text-cyan-300 shrink-0 animate-spin" />;
  }
  if (status === "failed") {
    return <XCircle className="w-3.5 h-3.5 text-rose-400 shrink-0" />;
  }
  return <Circle className="w-3.5 h-3.5 text-[#647087] shrink-0" />;
}

function AudienceTile({ label, description, selected, disabled, onSelect, testId, tone }) {
  const ringColor = tone === "rose" ? "rose" : "emerald";
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onSelect}
      disabled={disabled}
      data-testid={testId}
      className={`p-3 rounded-md border text-left transition ${
        selected
          ? `border-${ringColor}-400/60 bg-${ringColor}-500/10`
          : "border-white/10 hover:border-white/25 bg-white/[0.02]"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <div className={`text-xs font-semibold text-${ringColor}-300`}>{label}</div>
      <div className="text-[10px] text-[#a0aabf] mt-1 leading-snug">{description}</div>
    </button>
  );
}

function ProjectLinkCard({ projects, projectId, onChange, category }) {
  // Quotes / sales orders are WIP; invoices/CR drive final profit. Make
  // the visual hint explicit so the user knows what this link does.
  const wip = category === "quote" || category === "sales_order";
  const finalCat = category === "invoice" || category === "credit_note";
  return (
    <section className="hv-card !p-4 space-y-2" data-testid="sales-doc-project-link">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#1FB6C1]" /> Project link <span className="text-[10px] text-[#647087] font-normal">(optional)</span>
        </h2>
        {wip && <span className="text-[10px] text-amber-300">WIP doc — feeds tracking only</span>}
        {finalCat && <span className="text-[10px] text-emerald-300">Final doc — counts in project profit</span>}
      </header>
      <SearchableSelect
        value={projectId || ""}
        onChange={onChange}
        options={projects.map((p) => ({
          id: p.id,
          label: `${p.short_id ? `${p.short_id} — ` : ""}${p.title}`,
          sublabel: `${(p.status || "—").toUpperCase()}${p.customer_name ? ` · ${p.customer_name}` : ""}`,
          searchTerms: [p.short_id, p.title, p.customer_name, p.status].filter(Boolean),
        }))}
        placeholder="— No project (general / accounting only) —"
        testId="sales-doc-project-select"
        emptyHint="No project matches — try short_id or customer name."
      />
      <p className="text-[10px] text-[#647087] leading-snug">
        Quotes and sales orders are tracked against the project as WIP. The <b>final invoice + credit notes + expenses</b> linked here are what determines the project's profit when work is done.
      </p>
    </section>
  );
}

function ProfitabilityCard({ lineItems, category, amount }) {
  // Compute revenue/cost/profit from the line_items inline so the user
  // sees margin instantly when a quote is opened. Cost is the avg_cost
  // stamped at OCR time (`matched_avg_cost`); if absent we mark the line
  // "cost unknown" but still count revenue so the operator sees a hint.
  if (!Array.isArray(lineItems) || lineItems.length === 0) return null;
  let revenue = 0;
  let cost = 0;
  let unknown = 0;
  let accepted = 0;
  for (const ln of lineItems) {
    if (!ln || (ln.action || "accept").toLowerCase() === "ignore") continue;
    accepted += 1;
    const qty = Number(ln.quantity ?? ln.qty ?? 0) || 0;
    const price = Number(ln.unit_price ?? 0) || 0;
    const avg = Number(ln.matched_avg_cost ?? 0) || 0;
    revenue += qty * price;
    if (avg > 0) cost += qty * avg;
    else unknown += 1;
  }
  // Fall back to header amount if revenue couldn't be computed from lines.
  const headerAmount = Number(amount) || 0;
  if (!revenue && headerAmount) revenue = headerAmount;
  const profit = revenue - cost;
  const gp = revenue > 0 ? (profit / revenue) * 100 : null;
  const fmt = (n) => `R ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const tone = gp == null ? "text-[#647087]" : (gp >= 25 ? "text-emerald-300" : gp >= 10 ? "text-amber-300" : "text-rose-300");
  const wip = category === "quote" || category === "sales_order";
  return (
    <section className="hv-card !p-4 space-y-3 border border-[#1FB6C1]/30" data-testid="sales-doc-profitability">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <span className="text-[#1FB6C1]">●</span> Live profitability <span className="text-[10px] text-[#647087] font-normal">({accepted} line{accepted === 1 ? "" : "s"} · costs from inventory.avg_cost)</span>
        </h2>
        {wip && <span className="text-[10px] text-amber-300 uppercase tracking-wider">WIP estimate</span>}
      </header>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Revenue" value={fmt(revenue)} testId="profit-revenue" />
        <Stat label="Cost (avg)" value={fmt(cost)} testId="profit-cost" />
        <Stat label="Profit" value={fmt(profit)} className={profit >= 0 ? "text-emerald-300" : "text-rose-300"} testId="profit-profit" />
        <Stat label="GP %" value={gp != null ? `${gp.toFixed(1)}%` : "—"} className={tone} testId="profit-gp" />
      </div>
      {unknown > 0 && (
        <p className="text-[10px] text-amber-300 leading-snug">
          ⚠ {unknown} line{unknown === 1 ? " has" : "s have"} no matching inventory cost on file — counted as zero cost. Match the line to an SKU on the scan, or update avg_cost on the inventory item, for an accurate margin.
        </p>
      )}
      <p className="text-[10px] text-[#647087] leading-snug">
        Cost is computed live from the inventory <span className="font-mono">avg_cost</span> at the moment you open this doc — not frozen at scan time. Update inventory cost to refresh the margin.
      </p>
    </section>
  );
}

function Stat({ label, value, className = "text-white", testId }) {
  return (
    <div className="border border-white/10 rounded px-2 py-1.5 bg-white/[0.02]" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wider text-[#647087]">{label}</div>
      <div className={`text-sm font-mono ${className}`}>{value}</div>
    </div>
  );
}



/**
 * PushToPickingSlipCard — Iter 395.
 *
 * Rendered inside SalesDocEditor when the doc is an existing sales_order.
 * Shows current links + a one-click "Push to picking slip" that either
 * creates a new slip or appends to an existing linked one.
 */
function PushToPickingSlipCard({ salesDocId, customerId }) {
  const [linkedLists, setLinkedLists] = useState([]);
  const [projectSlips, setProjectSlips] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!salesDocId) return;
      setLoading(true);
      try {
        const [linksRes, docRes] = await Promise.all([
          api.get(`/entity-links/sales_doc/${salesDocId}`),
          api.get(`/sales-documents/${salesDocId}`),
        ]);
        if (cancelled) return;
        setLinkedLists(linksRes.data?.picking || []);
        const pid = docRes.data?.project_id || null;
        setProjectId(pid);
        // Also fetch OTHER picking slips on the same project the user
        // might want to append to (not just already-linked ones).
        if (pid) {
          try {
            const { data: pdata } = await api.get(`/picking/lists`, { params: { project_id: pid, limit: 100 } });
            const all = (pdata?.items || []).filter(
              (p) => !(linksRes.data?.picking || []).some((l) => l.id === p.id),
            );
            if (!cancelled) setProjectSlips(all);
          } catch { /* noop */ }
        }
      } catch {
        if (!cancelled) { setLinkedLists([]); setProjectSlips([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [salesDocId]);

  const pushNew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/picking/from-sales-order`, { sales_doc_id: salesDocId });
      toast.success(`Created picking slip — ${data?.items_created ?? 0} rows loaded.`);
      const listId = data?.list?.id;
      if (listId) nav(`/picking/lists/${listId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Push failed.");
    } finally {
      setBusy(false);
    }
  };

  const pushExisting = async (lid, listLabel) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/picking/lists/${lid}/pull-from-sales-doc`, { sales_doc_id: salesDocId });
      const added = data?.added ?? 0;
      toast.success(`Pulled ${added} row${added === 1 ? "" : "s"} into ${listLabel || "picking slip"}.`);
      nav(`/picking/lists/${lid}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Push failed.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  const allTargets = [
    ...linkedLists.map((l) => ({ ...l, __linked: true })),
    ...projectSlips.map((l) => ({ ...l, __label: l.name, __status: l.status, __linked: false })),
  ];

  return (
    <section
      className="hv-card !p-4 space-y-3 border border-emerald-500/20 bg-emerald-500/5"
      data-testid="sales-doc-push-picking-card"
    >
      <h2 className="text-xs uppercase tracking-[0.2em] text-emerald-300 font-bold flex items-center gap-2">
        <ClipboardList className="w-3.5 h-3.5" /> Push to picking slip
      </h2>
      <p className="text-[11px] text-[#a0aabf]">
        Send this sales order&apos;s line items to the warehouse. Idempotent — sending twice accumulates qty instead of duplicating rows.
      </p>
      {allTargets.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
            Add to existing picking slip
          </div>
          {allTargets.map((pl) => (
            <div
              key={pl.id}
              className="flex items-center gap-2 p-2 rounded border border-white/5 bg-white/[0.02]"
              data-testid={`sales-doc-push-target-${pl.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                  {pl.short_id && (
                    <span className="text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border border-cyan-400/40 text-cyan-300 bg-cyan-500/10">
                      {pl.short_id}
                    </span>
                  )}
                  {pl.__linked && (
                    <span className="text-[9px] uppercase tracking-wider text-emerald-300 font-mono">linked</span>
                  )}
                  {(pl.__status || pl.status) && (
                    <span className="text-[9px] uppercase tracking-wider text-[#647087] font-mono">
                      {String(pl.__status || pl.status).replace(/_/g, " ")}
                    </span>
                  )}
                </div>
                <div className="text-xs text-white truncate">{pl.__label || pl.name || "Picking slip"}</div>
              </div>
              <button
                type="button"
                onClick={() => pushExisting(pl.id, pl.__label || pl.name)}
                disabled={busy}
                className="hv-btn hv-btn-ghost text-[11px] text-emerald-300 disabled:opacity-50 shrink-0"
                data-testid={`sales-doc-push-existing-${pl.id}`}
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add rows →"}
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-end pt-1">
        <button
          type="button"
          onClick={pushNew}
          disabled={busy}
          className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
          data-testid="sales-doc-push-new-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5" />}
          Create new picking slip
        </button>
      </div>
    </section>
  );
}

/**
 * ExtractedContentPanel — Iter 411 (Option A unification).
 *
 * Rendered inside SalesDocEditor when editing an existing sales doc. Its
 * job is to close the gap between the "Bookkeep/Job Card" view (which
 * historically only showed the metadata form) and the "Customer Accounts"
 * view (which shows every OCR-extracted field inline).
 *
 * Displays:
 *   • Line items table — pulled from doc.line_items, always available once
 *     background OCR has finished. Shows qty, description/SKU, unit price,
 *     line total, and match confidence to the inventory master.
 *   • OCR snapshot — bill-to, supplier, totals, statement summary — hydrated
 *     from the linked OCR job (invoice_ocr_jobs) via
 *     GET /ocr/customer-documents?customer_id=X (matched by
 *     source_sales_document_id === did).
 *   • Deep link into the full scanner for row-level review / chip actions.
 */
function ExtractedContentPanel({ did, customerId, category, lineItems }) {
  const [ocrDoc, setOcrDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!customerId || !did) {
      setLoading(false);
      return () => {};
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/ocr/customer-documents", {
          params: { customer_id: customerId },
        });
        const list = Array.isArray(data) ? data : [];
        const match = list.find((d) => d.source_sales_document_id === did);
        if (!cancelled) setOcrDoc(match || null);
      } catch {
        if (!cancelled) setOcrDoc(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId, did]);

  const lines = useMemo(() => (Array.isArray(lineItems) ? lineItems : []), [lineItems]);
  const totals = ocrDoc?.totals || {};
  const billTo = ocrDoc?.bill_to || null;
  const supplier = ocrDoc?.supplier || null;
  const stmt = ocrDoc?.statement || null;

  // Fallback totals from the line items themselves so the user still gets
  // a subtotal when the linked OCR job hasn't finished (or was never run).
  const linesSubtotal = useMemo(() => {
    let s = 0;
    for (const ln of lines) {
      if (!ln || (ln.action || "accept").toLowerCase() === "ignore") continue;
      const qty = Number(ln.quantity ?? ln.qty ?? 0) || 0;
      const price = Number(ln.unit_price ?? 0) || 0;
      const lt = Number(ln.line_total ?? 0) || qty * price;
      s += lt;
    }
    return s;
  }, [lines]);

  const hasAnything = lines.length > 0 || ocrDoc;
  if (!hasAnything && !loading) {
    return (
      <section
        className="hv-card !p-4 space-y-2 border border-white/5"
        data-testid="sales-doc-extracted-empty"
      >
        <h2 className="text-xs uppercase tracking-[0.2em] text-[#647087] font-bold flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Extracted content
        </h2>
        <p className="text-[11px] text-[#647087] leading-snug">
          No OCR line items or extracted fields on this document yet. Upload
          a PDF with a clear header + line table, or run <em>Scan with OCR</em>
          from the Customer Accounts view.
        </p>
      </section>
    );
  }

  const fmt = (n) => (n == null || n === "" ? "—" : `R ${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 2 })}`);

  return (
    <section
      className="hv-card !p-4 space-y-3 border border-[#1FB6C1]/25"
      data-testid="sales-doc-extracted-panel"
    >
      <header className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xs uppercase tracking-[0.2em] text-[#1FB6C1] font-bold flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Extracted content
          {loading && <Loader2 className="w-3 h-3 animate-spin text-[#647087]" />}
        </h2>
        {/* Iter 411 — "Open full scanner" always renders on any doc
            that has line_items (or a linked OCR job). The full scanner
            is where the operator FIXES OCR mistakes: re-map wrong
            SKUs, match unmatched lines to inventory, split/merge
            rows the vision pass got wrong, and re-run per-line chip
            actions (equipment / analysis / job card / ticket).
            Previously this only rendered when `ocrDoc?.id` was set
            — so orphaned sales docs (line_items on the sales doc but
            the OCR-job record cleaned up) had NO way to reach the
            editor. Dirk (2026-02-28): "where is the button to open
            the full scanner to edit the lines it got wrong?" */}
        {(ocrDoc?.id || (Array.isArray(lineItems) && lineItems.length > 0)) && (
          <a
            href={
              ocrDoc?.id
                ? `/ocr/invoices?customer_id=${customerId}&sales_doc=${did}&job=${ocrDoc.id}`
                : `/ocr/invoices?customer_id=${customerId}&sales_doc=${did}`
            }
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-[#1FB6C1]/40 bg-[#1FB6C1]/10 text-[#1FB6C1] hover:bg-[#1FB6C1]/20 hover:border-[#1FB6C1]/60 text-[11px] font-medium transition-colors"
            data-testid="sales-doc-extracted-open-full-scanner"
            title="Open full scanner — fix wrong SKUs, match unmatched lines to inventory, split/merge rows, chip actions"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open full scanner
            {(() => {
              const unmatched = (lineItems || []).filter(
                (ln) => !ln?.matched_inventory_id && (ln?.action || "accept").toLowerCase() !== "ignore",
              ).length;
              if (unmatched > 0) {
                return (
                  <span
                    className="ml-1 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-400/40"
                    title={`${unmatched} line${unmatched === 1 ? "" : "s"} have no inventory match — click to fix in the full scanner.`}
                  >
                    {unmatched} to match
                  </span>
                );
              }
              return null;
            })()}
          </a>
        )}
      </header>

      {/* Bill-to / Supplier / OCR header snapshot */}
      {(billTo || supplier || ocrDoc?.invoice_number || ocrDoc?.invoice_date) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] bg-black/20 rounded-md p-2 border border-white/5" data-testid="sales-doc-extracted-header">
          {supplier && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-[#647087]">Supplier / issuer</div>
              <div className="text-white">{supplier}</div>
            </div>
          )}
          {billTo?.name && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-[#647087]">Bill-to</div>
              <div className="text-white flex items-center gap-1.5">
                <UserIcon className="w-3 h-3 text-[#647087]" /> {billTo.name}
                {billTo.vat_number && <span className="font-mono text-[#647087] ml-1">VAT {billTo.vat_number}</span>}
              </div>
            </div>
          )}
          {ocrDoc?.invoice_number && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-[#647087]">Extracted #</div>
              <div className="font-mono text-white">{ocrDoc.invoice_number}</div>
            </div>
          )}
          {ocrDoc?.invoice_date && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-[#647087]">Extracted date</div>
              <div className="font-mono text-white">{ocrDoc.invoice_date}</div>
            </div>
          )}
        </div>
      )}

      {/* Statement snapshot */}
      {stmt && (stmt.closing_balance != null || stmt.total_due != null) && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2 space-y-1" data-testid="sales-doc-extracted-statement">
          <div className="text-[10px] uppercase tracking-wider text-amber-300 flex items-center gap-1">
            <FileWarning className="w-3 h-3" /> Statement snapshot
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
            <ExtKV label="Closing balance" value={fmt(stmt.closing_balance)} />
            <ExtKV label="Total due" value={fmt(stmt.total_due)} />
            <ExtKV label="Account status" value={stmt.account_status || "—"} />
            <ExtKV label="As of" value={stmt.statement_date || "—"} />
          </div>
        </div>
      )}

      {/* Line items table */}
      {lines.length > 0 && (
        <div className="space-y-1.5" data-testid="sales-doc-extracted-lines">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex items-center gap-1.5">
              <Package className="w-3 h-3" /> Line items <span className="text-[#a0aabf] font-normal">({lines.length})</span>
            </div>
            {linesSubtotal > 0 && (
              <div className="text-[10px] text-[#a0aabf]">
                Subtotal <span className="font-mono text-white ml-1">{fmt(linesSubtotal)}</span>
              </div>
            )}
          </div>
          <div className="overflow-x-auto border border-white/5 rounded-md">
            {/* Iter 411 — Desktop table (>= sm). On mobile the columns
                get so narrow that header text stacks letter-by-letter
                (Dirk, 2026-02-28) — so on small screens we swap to a
                stacked card layout below. */}
            <table className="hidden sm:table min-w-full text-[11px]" data-testid="sales-doc-extracted-lines-table">
              <thead className="bg-white/[0.03] text-[9px] uppercase tracking-wider text-[#647087]">
                <tr>
                  <th className="text-left px-2 py-1.5">Qty</th>
                  <th className="text-left px-2 py-1.5">Description / SKU</th>
                  <th className="text-right px-2 py-1.5">Unit price</th>
                  <th className="text-right px-2 py-1.5">Line total</th>
                  <th className="text-left px-2 py-1.5">Match</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((ln, i) => {
                  const qty = Number(ln.quantity ?? ln.qty ?? 0) || 0;
                  const price = Number(ln.unit_price ?? 0) || 0;
                  const total = Number(ln.line_total ?? 0) || qty * price;
                  const conf = Number(ln.matched_confidence ?? 0) || 0;
                  const matched = !!ln.matched_inventory_id;
                  const action = (ln.action || "accept").toLowerCase();
                  const ignored = action === "ignore";
                  return (
                    <tr
                      key={i}
                      className={`border-t border-white/5 ${ignored ? "opacity-40 line-through" : ""}`}
                      data-testid={`sales-doc-extracted-line-${i}`}
                    >
                      <td className="px-2 py-1.5 font-mono tabular-nums text-white">{qty || "—"}</td>
                      <td className="px-2 py-1.5">
                        <div className="text-white truncate max-w-[28rem]">{ln.name || ln.raw_text || "—"}</div>
                        {ln.sku && (
                          <div className="text-[9px] font-mono text-[#647087]">SKU {ln.sku}</div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 font-mono tabular-nums text-right text-[#a0aabf]">{fmt(price)}</td>
                      <td className="px-2 py-1.5 font-mono tabular-nums text-right text-white">{fmt(total)}</td>
                      <td className="px-2 py-1.5">
                        {matched ? (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded border border-emerald-400/40 text-emerald-300 bg-emerald-500/10"
                            title={`Matched inventory (confidence ${Math.round(conf * 100)}%)`}
                          >
                            <CheckCircle2 className="w-2.5 h-2.5" /> matched
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded border border-amber-400/40 text-amber-300 bg-amber-500/10"
                            title="No inventory match — cost is unknown for profitability."
                          >
                            <AlertTriangle className="w-2.5 h-2.5" /> unmatched
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Iter 411 — Mobile stacked-card layout (< sm). Each row
                becomes its own card so numbers never get squeezed to
                the point of stacking letter-per-line vertically. */}
            <div className="sm:hidden divide-y divide-white/5">
              {lines.map((ln, i) => {
                const qty = Number(ln.quantity ?? ln.qty ?? 0) || 0;
                const price = Number(ln.unit_price ?? 0) || 0;
                const total = Number(ln.line_total ?? 0) || qty * price;
                const matched = !!ln.matched_inventory_id;
                const action = (ln.action || "accept").toLowerCase();
                const ignored = action === "ignore";
                return (
                  <div
                    key={i}
                    className={`p-2 space-y-1 ${ignored ? "opacity-40 line-through" : ""}`}
                    data-testid={`sales-doc-extracted-line-m-${i}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-[11px] font-mono tabular-nums text-white shrink-0 min-w-[2rem]">{qty || "—"}×</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-white break-words">{ln.name || ln.raw_text || "—"}</div>
                        {ln.sku && <div className="text-[9px] font-mono text-[#647087]">SKU {ln.sku}</div>}
                      </div>
                      {matched ? (
                        <span className="text-[9px] font-mono px-1 py-0.5 rounded border border-emerald-400/40 text-emerald-300 bg-emerald-500/10 shrink-0">✓</span>
                      ) : (
                        <span className="text-[9px] font-mono px-1 py-0.5 rounded border border-amber-400/40 text-amber-300 bg-amber-500/10 shrink-0" title="Unmatched">!</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-[10px] font-mono text-[#a0aabf] pl-8">
                      <span>@ {fmt(price)}</span>
                      <span className="text-white">{fmt(total)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* OCR totals */}
      {(totals.subtotal != null || totals.tax != null || totals.total != null) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] bg-black/20 rounded-md p-2 border border-white/5" data-testid="sales-doc-extracted-totals">
          <ExtKV label="Subtotal" value={fmt(totals.subtotal)} />
          <ExtKV label="Tax / VAT" value={fmt(totals.tax)} />
          <ExtKV label="Total" value={fmt(totals.total || totals.grand_total)} />
          <ExtKV label="Currency" value={ocrDoc?.currency || "ZAR"} />
        </div>
      )}

      {/* When there's no linked OCR job yet, hint the operator */}
      {!ocrDoc && !loading && lines.length === 0 && (
        <p className="text-[10px] text-[#647087] leading-snug">
          Background OCR hasn&apos;t produced structured line items for this doc yet.
          If a PDF is attached, wait a moment and refresh, or trigger <em>Scan
          with OCR</em> from the Customer Accounts view.
        </p>
      )}
    </section>
  );
}

function ExtKV({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-[#647087]">{label}</div>
      <div className="text-white font-mono text-[11px]">{value}</div>
    </div>
  );
}

